import { Navigation } from "@/components/Navigation"
import { Footer } from "@/components/Footer"
import { Button } from "@/components/ui/button"
import { ArrowRight, CheckCircle2 } from "lucide-react"
import { Link } from "react-router-dom"
import { PageFAQ, FAQItem } from "@/components/PageFAQ"

interface ServiceLayoutProps {
  subtitle?: string
  title: string
  description: string
  heroImage: string
  features: {
    title: string
    description: string
  }[]
  benefits: string[]
  approach?: {
    title: string
    description: string
    image?: string
    content: string | string[]
  }
  process?: {
    steps: {
      title: string
      description: string
    }[]
  }
  techStack?: {
    category: string
    items: string[]
  }[]
  faqs?: FAQItem[]
  faqTitle?: string
  faqSubtitle?: string
  ctaText?: string
  ctaLink?: string
}

import { SEO } from "@/components/SEO"

export const ServiceLayout = ({
  title,
  subtitle,
  description,
  heroImage,
  features,
  benefits,
  approach,
  process,
  techStack,
  faqs,
  faqTitle,
  faqSubtitle,
  ctaText = "Start Your Transformation",
  ctaLink = "/contact",
}: ServiceLayoutProps) => {
  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={`${title} | RSA Tech Group`}
        description={description}
        image={heroImage}
      />
      <Navigation />

      {/* Hero Section */}
      <div className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden bg-black text-white">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-black z-0" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/20 rounded-full blur-[120px] pointer-events-none" />

        <div className="section-container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            {subtitle && (
              <span className="inline-block py-1 px-3 rounded-full bg-accent/10 border border-accent/20 text-accent font-medium text-sm mb-6 tracking-wide uppercase">
                {subtitle}
              </span>
            )}
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-8 tracking-tight">
              {title}
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed max-w-2xl mx-auto mb-10">
              {description}
            </p>
            <Link to={ctaLink} className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-white transition-all duration-200 bg-gradient-to-r from-blue-600 via-blue-600 to-orange-500 rounded-lg hover:brightness-110 shadow-lg">
              {ctaText} <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <section className="py-24 bg-white">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-24">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-tr from-accent/20 to-primary/20 rounded-2xl transform -rotate-2" />
              <img
                src={heroImage}
                alt={title}
                className="relative rounded-xl shadow-2xl w-full object-cover aspect-[4/3]"
              />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Key <span className="text-primary">Capabilities</span>
              </h2>
              <div className="space-y-8">
                {features.map((feature, idx) => (
                  <div key={idx} className="flex gap-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/5 flex items-center justify-center flex-shrink-0">
                      <span className="text-xl font-bold text-primary">
                        {idx + 1}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Approach Section */}
          {approach && (
            <div className="mb-24 bg-gray-50 rounded-2xl p-8 md:p-12 border border-gray-100">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div className="order-last lg:order-first">
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">
                    {approach.title}
                  </h2>
                  <p className="text-lg text-primary font-medium mb-6">
                    {approach.description}
                  </p>
                  <div className="prose prose-lg text-gray-600">
                    {Array.isArray(approach.content) ? (
                      approach.content.map((paragraph, index) => (
                        <p key={index} className="mb-4 last:mb-0">
                          {paragraph}
                        </p>
                      ))
                    ) : (
                      <p>{approach.content}</p>
                    )}
                  </div>
                </div>
                <div className="relative h-full min-h-[300px] rounded-xl overflow-hidden shadow-lg">
                  <img
                    src={approach.image || heroImage}
                    alt="Approach"
                    className="absolute inset-0 w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-60" />
                </div>
              </div>
            </div>
          )}

          {/* Process Section */}
          {process && (
            <div className="mb-24">
              <div className="text-center max-w-3xl mx-auto mb-16">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Our <span className="text-accent">Methodology</span>
                </h2>
                <p className="text-gray-600">
                  A structured approach to delivery that ensures consistency and
                  quality.
                </p>
              </div>
              <div className="relative">
                {/* Connecting Line */}
                <div className="absolute top-1/2 left-0 w-full h-0.5 bg-gray-100 -translate-y-1/2 hidden lg:block" />

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                  {process.steps.map((step, idx) => (
                    <div
                      key={idx}
                      className="relative bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow"
                    >
                      <div className="w-10 h-10 bg-accent text-white rounded-full flex items-center justify-center font-bold mb-4 relative z-10 mx-auto lg:mx-0">
                        {idx + 1}
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2 text-center lg:text-left">
                        {step.title}
                      </h3>
                      <p className="text-sm text-gray-600 text-center lg:text-left leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Tech Stack Section */}
          {techStack && (
            <div className="mb-24">
              <div className="text-center max-w-3xl mx-auto mb-16">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Technology <span className="text-primary">Stack</span>
                </h2>
                <p className="text-gray-600">
                  Built on modern, enterprise-grade frameworks and
                  infrastructure.
                </p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {techStack.map((stack, idx) => (
                  <div key={idx} className="bg-gray-50 rounded-xl p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2">
                      {stack.category}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {stack.items.map((item, i) => (
                        <span
                          key={i}
                          className="px-3 py-1 bg-white border border-gray-200 rounded-full text-sm text-gray-600 font-medium"
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Benefits Section */}
          <div className="bg-gray-50 rounded-2xl p-8 md:p-12">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Why Choose <span className="text-accent">RSA Tech Group</span>
              </h2>
              <p className="text-gray-600">
                Delivering measurable impact through verified engineering
                excellence.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((benefit, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-3 bg-white p-6 rounded-xl shadow-sm border border-gray-100"
                >
                  <CheckCircle2 className="h-6 w-6 text-green-500 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-24 text-center">
            <Link to={ctaLink}>
              <Button
                size="xl"
                className="font-bold text-lg px-8 py-6 rounded-full shadow-lg hover:shadow-primary/20 transition-all"
              >
                {ctaText} <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      {faqs && faqs.length > 0 && (
        <PageFAQ
          faqs={faqs}
          title={faqTitle || `${title} FAQs`}
          subtitle={faqSubtitle || `Common questions about ${title.toLowerCase()}`}
          variant="muted"
        />
      )}

      <Footer />
    </div>
  )
}
